import { Box, Typography, useTheme } from "@mui/material";
import { hasValue, maskMinimumValue, TOOLTIP_MAX_WIDTH } from "../utils";

/** Shown for periods the data source reported no value for. */
const NO_DATA_PLACEHOLDER = "—";

interface TooltipPayloadEntry<T> {
  payload?: T;
}

interface LineChartTooltipProps<T> {
  active?: boolean;
  payload?: Array<TooltipPayloadEntry<T>>;
  series: Array<{ key: keyof T; label: string; color?: string }>;
  minYAxisValue?: number;
  tooltipLabel?: (point: T) => string;
  /**
   * Drops the tooltip entirely on a period none of the visible series reported,
   * rather than showing a row of placeholders.
   */
  hideWithoutValue?: boolean;
}

export const LineChartTooltip = <T,>({
  active,
  payload,
  series,
  minYAxisValue,
  tooltipLabel,
  hideWithoutValue = false,
}: LineChartTooltipProps<T>): React.ReactElement | null => {
  const theme = useTheme();
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload as T;

  if (hideWithoutValue && !series.some((s) => hasValue(point[s.key]))) {
    return null;
  }

  const pointLabel = tooltipLabel
    ? tooltipLabel(point)
    : (point as { date?: string }).date;

  return (
    <Box
      sx={{
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          p: 1.5,
          borderRadius: "8px",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
          maxWidth: TOOLTIP_MAX_WIDTH,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 13,
            mb: 1,
            wordBreak: "break-word",
            color: theme.colors.text[6],
          }}
        >
          {pointLabel}
        </Typography>
        {series.map((s) => (
          <Box
            key={String(s.key)}
            sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                backgroundColor: s.color,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: 12, color: theme.colors.text[6] }}>
              {s.label}:{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {hasValue(point[s.key])
                  ? maskMinimumValue(Number(point[s.key]), minYAxisValue)
                  : NO_DATA_PLACEHOLDER}
              </Box>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
